// Generic inline card for device types without a dedicated control card, so the
// grid NEVER opens a side window. It fetches the device's outputs: if there are
// on/off relays it renders a toggle per output (+ All On/Off); otherwise (or when
// infoOnly, e.g. read-only energy meters) it shows a compact info tile.

import React, { useEffect, useState } from 'react';
import { Card, Box, Typography, Button } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import BoltIcon from '@mui/icons-material/Bolt';
import { useApp } from '../state/AppContext';
import { useDeviceStatus } from '../mqtt/useDeviceStatus';
import { parseStatus } from '../mqtt/mqttClient';
import { publishCommand } from '../utils/deviceController';
import { deviceTypeKey } from '../devices/deviceMeta';
import { getOutputs, getDeviceStatus } from '../api/api';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';

function parseStates(status, n) {
  if (!status) return Array(n).fill(false);
  if (n === 1) {
    const p = status.split('-');
    return [p[p.length - 1] !== '0'];
  }
  const p = status.split(',');
  return Array.from({ length: n }, (_, i) => p[i] === '1');
}

export default function GenericControlCard({ device, subtitle, infoOnly = false }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [outputs, setOutputs] = useState([]);
  const [loaded, setLoaded] = useState(infoOnly);
  // Seed from the already-loaded device row (list includes `Status`) so states
  // reflect reality immediately; live MQTT then keeps them current.
  const [status, setStatus] = useDeviceStatus(uno, parseStatus(device.Status || device.DeviceStatus || ''));
  const [states, setStates] = useState([]);

  useEffect(() => {
    if (infoOnly) return undefined;
    let alive = true;
    (async () => {
      const [out, st] = await Promise.all([
        getOutputs(uno).catch(() => null),
        getDeviceStatus(uno).catch(() => null),
      ]);
      if (!alive) return;
      const list = out && Array.isArray(out.data) ? out.data : [];
      setOutputs(list);
      setStates(Array(list.length).fill(false));
      const row = st && Array.isArray(st.data) ? st.data[0] : null;
      if (row && row.Status) {
        const db = parseStatus(row.Status);
        if (db) setStatus((cur) => cur || db);
      }
      setLoaded(true);
    })();
    return () => { alive = false; };
  }, [uno, infoOnly, setStatus]);

  useEffect(() => {
    if (status && outputs.length) setStates(parseStates(status, outputs.length));
  }, [status, outputs.length]);

  const n = outputs.length;
  const toggle = (i) => {
    const next = !states[i];
    setStates((prev) => { const u = [...prev]; u[i] = next; return u; });
    publishCommand(uno, `${i + 1}-${next ? 1 : 0}`, userId);
  };
  const setAll = (on) => {
    setStates(Array(n).fill(on));
    publishCommand(uno, on ? 'allon' : 'alloff', userId);
  };
  const outName = (i) => {
    const o = outputs.find((x) => Number(x.Output) === i + 1);
    return (o && o.OutputName) || `Switch ${i + 1}`;
  };

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>{subtitle}</Typography>
        )}
        <Typography fontWeight={700} color="text.primary" noWrap>{device.DeviceName || uno}</Typography>
      </Box>
      <DeviceStatusDot device={device} live={status} />
      <DeviceCardMenu device={device} />
    </Box>
  );

  const infoTile = () => {
    const meter = deviceTypeKey(device.DeviceID) === 'singlePhase' || deviceTypeKey(device.DeviceID) === 'threePhase';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, flex: 1, py: 2, color: 'text.secondary' }}>
        {meter ? <BoltIcon fontSize="large" /> : <DevicesOtherIcon fontSize="large" />}
        <Typography variant="caption">{meter ? 'Read-only meter' : 'No controllable outputs'}</Typography>
      </Box>
    );
  };

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      {header}

      {(!loaded) ? (
        <Box sx={{ flex: 1 }} />
      ) : n === 0 ? (
        infoTile()
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: n > 1 ? '1fr 1fr' : '1fr', gap: 1 }}>
            {Array.from({ length: n }, (_, i) => {
              const on = states[i];
              return (
                <Button
                  key={i} onClick={() => toggle(i)}
                  variant={on ? 'contained' : 'outlined'} color={on ? 'success' : 'inherit'}
                  startIcon={<PowerSettingsNewIcon />}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2, py: 0.5, color: on ? undefined : 'text.primary', borderColor: on ? undefined : 'divider' }}
                >
                  <Box sx={{ minWidth: 0, textAlign: 'left', lineHeight: 1.1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{outName(i)}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{on ? 'ON' : 'OFF'}</Typography>
                  </Box>
                </Button>
              );
            })}
          </Box>
          {n > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
              <Button size="small" fullWidth variant="contained" onClick={() => setAll(true)}>All On</Button>
              <Button size="small" fullWidth variant="outlined" onClick={() => setAll(false)}>All Off</Button>
            </Box>
          )}
        </>
      )}

    </Card>
  );
}
